import '@/components/proposals/proposal-animations.css';

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ProposalListPanel from '@/components/proposals/list-panel/ProposalListPanel';
import ProposalListSidebar from '@/components/proposals/sidebar-list/ProposalListSidebar';
import { useOrg } from '@/contexts/OrgContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  type AiActionsView,
  DEFAULT_AI_ACTIONS_VIEW,
} from '@/lib/localStorage/aiActionsSortPrefs';

interface ActionsPageProps {
  /** Which list this route shows. The view lives in the URL — /actions is
      always Open; /actions/done|dismissed|archived pass their view here. */
  view?: AiActionsView;
}

// NOTE: this page deliberately does NOT render the "Getting to know your
// business" onboarding import block (OnboardingImportState). It took too much
// vertical space above the action list, which is the point of this view. The
// import-status / recent-items / dismissal plumbing that fed it was removed
// with it rather than left dangling — reinstate all of it together if the
// block ever comes back.
//
// The legacy shell (LegacyActionsPage -> ProposalCenter) still renders it and
// is unaffected.
//
// View-health markers for this route are fired from the components that
// actually hold the data/render state: ProposalListPanel fires
// `actions-list-ready` / `actions-list-empty-ready` (owns the list query) and
// `actions-list-items-rendered` (post-commit of its own render). See route
// config at `dashboard.routes.tsx` → `actions`.
export default function ActionsPage({
  view = DEFAULT_AI_ACTIONS_VIEW,
}: ActionsPageProps) {
  // Below `md` the full-page grouped table doesn't fit — render the proven
  // narrow list (the same ProposalListSidebar the detail page uses as its
  // rail and the legacy shell uses on small screens) full-width instead
  // (product decision 2026-09-01). The sidebar owns its own view tabs; the
  // URL stays the source of truth via viewOverride + route navigation.
  const isDesktop = useBreakpoint('md');
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrg } = useOrg();

  const handleMobileActionSelect = useCallback(
    (actionId: number) => {
      if (currentOrg) {
        // backPath mirrors ProposalListPanel's row clicks so the detail
        // page's back chevron returns to this list deterministically.
        navigate(`/${currentOrg.id}/actions/${actionId}`, {
          state: { backPath: location.pathname },
        });
      }
    },
    [navigate, currentOrg, location.pathname]
  );

  const handleMobileViewChange = useCallback(
    (next: AiActionsView) => {
      if (currentOrg) {
        navigate(
          next === 'open'
            ? `/${currentOrg.id}/actions`
            : `/${currentOrg.id}/actions/${next}`
        );
      }
    },
    [navigate, currentOrg]
  );

  return (
    <div className='page-enter flex flex-col h-full min-h-0 overflow-hidden bg-bg-primary'>
      {/* flex is load-bearing: PageLayout's root is `flex-1` and only fills
          the viewport when its parent is a flex column. */}
      <div className='flex-1 min-h-0 flex flex-col'>
        {isDesktop ? (
          <ProposalListPanel view={view} />
        ) : (
          <ProposalListSidebar
            viewOverride={view}
            onActionSelect={handleMobileActionSelect}
            onViewChange={handleMobileViewChange}
          />
        )}
      </div>
    </div>
  );
}
